import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface SearchData {
  checkIn: string;
  checkOut: string;
  adults: number;
  children: number;
  babies: number;
  pets: number;
  numberOfNights?: number;
  totalGuests?: number;
  hotelPrice?: number;
  hotelId?: string;
  hotelName?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SearchService {
  private readonly searchDataSubject = new BehaviorSubject<SearchData | null>(null);
  public searchData$ = this.searchDataSubject.asObservable();

  constructor() { }

  // Guardar datos de búsqueda
  setSearchData(data: SearchData): void {
    // Calcular número de noches
    if (data.checkIn && data.checkOut) {
      const checkInDate = new Date(data.checkIn);
      const checkOutDate = new Date(data.checkOut);
      const timeDiff = checkOutDate.getTime() - checkInDate.getTime();
      data.numberOfNights = Math.ceil(timeDiff / (1000 * 3600 * 24));
    }

    // Calcular total de huéspedes
    data.totalGuests = data.adults + data.children + data.babies;

    this.searchDataSubject.next(data);
    
    // También guardar en localStorage para persistencia
    localStorage.setItem('searchData', JSON.stringify(data));
  }

  // Obtener datos de búsqueda
  getSearchData(): SearchData | null {
    const currentData = this.searchDataSubject.value;
    if (currentData) {
      return currentData;
    }

    // Si no hay datos en memoria, intentar cargar desde localStorage
    const savedData = localStorage.getItem('searchData');
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      this.searchDataSubject.next(parsedData);
      return parsedData;
    }

    return null;
  }

  // Limpiar datos de búsqueda
  clearSearchData(): void {
    this.searchDataSubject.next(null);
    localStorage.removeItem('searchData');
  }

  // Verificar si hay datos de búsqueda válidos
  hasValidSearchData(): boolean {
    const data = this.getSearchData();
    return data !== null && 
           data.checkIn !== '' && 
           data.checkOut !== '' && 
           data.adults > 0;
  }

  // Generar resumen de huéspedes
  getGuestsSummary(data?: SearchData): string {
    const searchData = data || this.getSearchData();
    if (!searchData) return '';

    const parts: string[] = [];
    
    if (searchData.adults > 0) {
      parts.push(`${searchData.adults} adulto${searchData.adults > 1 ? 's' : ''}`);
    }
    
    if (searchData.children > 0) {
      parts.push(`${searchData.children} niño${searchData.children > 1 ? 's' : ''}`);
    }
    
    if (searchData.babies > 0) {
      parts.push(`${searchData.babies} bebé${searchData.babies > 1 ? 's' : ''}`);
    }
    
    if (searchData.pets > 0) {
      parts.push(`${searchData.pets} mascota${searchData.pets > 1 ? 's' : ''}`);
    }
    
    return parts.length > 0 ? parts.join(' · ') : '2 adultos';
  }

  // Actualizar información del hotel
  updateHotelInfo(hotelId: string, hotelName: string, hotelPrice: number): void {
    const currentData = this.getSearchData();
    if (currentData) {
      const updatedData = {
        ...currentData,
        hotelId,
        hotelName,
        hotelPrice
      };
      this.setSearchData(updatedData);
    }
  }
}
